// FIN-13190 — rewrite every reference to a renamed function inside a formula tree.
//
// Renaming a function used to only patch the top level of `children1`, and only when the
// left-hand side was a bare string. It therefore missed:
//   * references nested inside a sub-group;
//   * references inside a function's arguments — so renaming a function used by
//     LINEAR_REGRESSION(val = <fn>) already left a dangling name before arithmetic existed,
//     and SUM / SUBTRACT / MULTIPLY widen that considerably;
//   * references on the right-hand side — `f2 > f1` silently broke when `f1` was renamed.
//
// It also mutated the nodes in place. `{ ...functionAggregation }` is a shallow copy, so the
// children were the very objects held in React state. These helpers rebuild the branches they
// touch and return the originals untouched everywhere else.
//
// Note on scope: the tree stores only the referenced *name*, so a reference to an event field
// that happens to share the function's name is indistinguishable from a reference to the
// function. On the Formula tab the function shadows the field anyway (the catalogue merges
// `allFunc` over `fields`), so rewriting every matching `valueSrc: 'field'` reference is the
// correct reading. Literals (`valueSrc: 'value'`) are never rewritten.

const isFuncValue = (value) =>
  value !== null &&
  typeof value === 'object' &&
  typeof value.func === 'string' &&
  value.args !== undefined;

// A function's arguments: { argName: { value, valueSrc } }.
const renameInFuncArgs = (args, oldName, newName) => {
  if (!args || typeof args !== 'object') return { args, changed: false };

  let changed = false;
  const next = {};

  Object.entries(args).forEach(([key, arg]) => {
    if (!arg || typeof arg !== 'object') {
      next[key] = arg;
      return;
    }

    if (arg.valueSrc === 'field' && arg.value === oldName) {
      next[key] = { ...arg, value: newName };
      changed = true;
      return;
    }

    // A nested function argument — recurse so SUBTRACT(SUM(f1, f2), f3) is fully rewritten.
    if (isFuncValue(arg.value)) {
      const nested = renameInFuncValue(arg.value, oldName, newName);
      next[key] = nested.changed ? { ...arg, value: nested.value } : arg;
      changed = changed || nested.changed;
      return;
    }

    next[key] = arg;
  });

  return { args: next, changed };
};

function renameInFuncValue(funcValue, oldName, newName) {
  const { args, changed } = renameInFuncArgs(funcValue.args, oldName, newName);
  return { value: changed ? { ...funcValue, args } : funcValue, changed };
}

// One side of a comparison: either a bare field name, or a { func, args } object.
const renameInOperand = (operand, valueSrc, oldName, newName) => {
  if (valueSrc === 'field' && operand === oldName) {
    return { operand: newName, changed: true };
  }
  if (isFuncValue(operand)) {
    const next = renameInFuncValue(operand, oldName, newName);
    return { operand: next.value, changed: next.changed };
  }
  return { operand, changed: false };
};

const renameInRule = (node, oldName, newName) => {
  const properties = node.properties;
  if (!properties) return { node, changed: false };

  let changed = false;
  const nextProperties = { ...properties };

  // Left-hand side. fieldSrc is 'field' for a bare name and 'func' for a function.
  const lhs = renameInOperand(
    properties.field,
    properties.fieldSrc === 'func' ? 'func' : 'field',
    oldName,
    newName
  );
  if (lhs.changed) {
    nextProperties.field = lhs.operand;
    changed = true;
  }

  // Right-hand side. `value` and `valueSrc` are parallel arrays.
  if (Array.isArray(properties.value)) {
    let rhsChanged = false;
    const nextValues = properties.value.map((v, i) => {
      const src = Array.isArray(properties.valueSrc)
        ? properties.valueSrc[i]
        : properties.valueSrc;
      const rhs = renameInOperand(v, src, oldName, newName);
      rhsChanged = rhsChanged || rhs.changed;
      return rhs.operand;
    });
    if (rhsChanged) {
      nextProperties.value = nextValues;
      changed = true;
    }
  }

  return changed
    ? { node: { ...node, properties: nextProperties }, changed }
    : { node, changed };
};

// children1 is an array in saved rules and an id-keyed object in some historical data.
const renameInChildren = (children, oldName, newName) => {
  if (Array.isArray(children)) {
    let changed = false;
    const next = children.map((child) => {
      const result = renameFunctionReferences(child, oldName, newName);
      changed = changed || result !== child;
      return result;
    });
    return changed ? next : children;
  }

  if (children && typeof children === 'object') {
    let changed = false;
    const next = {};
    Object.entries(children).forEach(([key, child]) => {
      const result = renameFunctionReferences(child, oldName, newName);
      changed = changed || result !== child;
      next[key] = result;
    });
    return changed ? next : children;
  }

  return children;
};

/**
 * Returns a tree with every reference to `oldName` rewritten to `newName`.
 * The original is returned unchanged (by identity) when nothing matched.
 */
export function renameFunctionReferences(node, oldName, newName) {
  if (!node || typeof node !== 'object') return node;
  if (!oldName || !newName || oldName === newName) return node;

  if (node.children1 !== undefined) {
    const children1 = renameInChildren(node.children1, oldName, newName);
    return children1 === node.children1 ? node : { ...node, children1 };
  }

  const { node: next } = renameInRule(node, oldName, newName);
  return next;
}

export default renameFunctionReferences;
